import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { GraduationCap, Loader2, Lock, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    if (password !== confirm) { toast.error("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password reset successful");
      setTimeout(() => navigate("/admin/login", { replace: true }), 1800);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not reset password.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 ptg-grid-bg p-4" data-testid="reset-password-page">
      <Seo title="Reset Password" />
      <div className="w-full max-w-md rounded-3xl bg-white p-8 sm:p-10 shadow-2xl">
        <div className="flex items-center gap-3 mb-8">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-900 text-white"><GraduationCap className="h-7 w-7" /></div>
          <div>
            <div className="font-heading text-xl font-extrabold text-slate-900">PTG Admin</div>
            <div className="text-xs font-semibold text-sky-700 uppercase tracking-widest">Password Reset</div>
          </div>
        </div>
        {!token ? (
          <div className="text-center" data-testid="reset-no-token">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto" />
            <p className="mt-4 text-slate-600">This reset link is invalid. Please request a new one.</p>
            <Link to="/admin/forgot-password" className="mt-6 inline-block text-sm font-semibold text-blue-900 hover:underline">Request a new link</Link>
          </div>
        ) : done ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle2 className="h-14 w-14 text-green-500 mx-auto" />
            <p className="mt-4 text-slate-600">Your password has been reset. Redirecting to login…</p>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Lock className="h-5 w-5 text-blue-900" /> Set a new password</h1>
            <p className="text-sm text-slate-500 mt-1">Choose a strong password for your admin account.</p>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-password-form">
              <div>
                <Label className="mb-1.5 block font-semibold text-slate-700">New Password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="reset-password" required />
              </div>
              <div>
                <Label className="mb-1.5 block font-semibold text-slate-700">Confirm Password</Label>
                <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" data-testid="reset-confirm" required />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-11 bg-blue-900 hover:bg-blue-950" data-testid="reset-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
